/**
 * Slot geometry helpers. Every layout expresses its slots as fractions (0..1) of its SCOPE box:
 * the whole spread (2 pages wide) or a single page. `materializeLayout` in @freealbum/document turns
 * them into mm frames.
 *
 * Because a spread box is twice as wide as it is tall (for a square format), horizontal fractions
 * are HALVED for spread-scope layouts so that a 4 % margin is the same number of millimetres on
 * both axes. `MARGIN_FRAC` is that 4 %.
 */
import type { Layout, LayoutFamily, LayoutScope, LayoutSlot } from '@freealbum/document';

/** Outer margin as a fraction of the page's short side. Shared by every layout. */
export const MARGIN_FRAC = 0.04;
/** Gap between neighbouring slots, same units as MARGIN_FRAC. */
export const GUTTER_FRAC = 0.025;
/** Extra breathing room on both sides of the fold in spread-scope layouts. */
export const FOLD_FRAC = 0.02;

export interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

const FULL: Box = { x: 0, y: 0, w: 1, h: 1 };

/** Horizontal shrink factor: spread boxes are twice as wide, so halve horizontal fractions. */
const hx = (scope: LayoutScope): number => (scope === 'spread' ? 0.5 : 1);

/** The content box once the outer margin is taken off. */
export function innerBox(scope: LayoutScope, margin = MARGIN_FRAC): Box {
  const mx = margin * hx(scope);
  return { x: mx, y: margin, w: 1 - 2 * mx, h: 1 - 2 * margin };
}

/** Map a 0..1 rectangle expressed inside `box` back to absolute scope fractions. */
export function sub(box: Box, x: number, y: number, w: number, h: number): Box {
  return { x: box.x + x * box.w, y: box.y + y * box.h, w: w * box.w, h: h * box.h };
}

/** Left / right half of a spread box, with the fold gutter removed. */
export function halves(box: Box, fold = FOLD_FRAC): [Box, Box] {
  const gap = fold * 0.5;
  const w = box.w / 2 - gap;
  return [
    { x: box.x, y: box.y, w, h: box.h },
    { x: box.x + box.w / 2 + gap, y: box.y, w, h: box.h },
  ];
}

export interface SlotInit extends Box {
  id: string;
  kind?: LayoutSlot['kind'];
  role?: LayoutSlot['role'];
  /** cover layouts only */
  panel?: LayoutSlot['panel'];
}

const round = (n: number): number => Math.round(n * 10000) / 10000;

export function slot(init: SlotInit): LayoutSlot {
  const base: LayoutSlot = {
    id: init.id,
    kind: init.kind ?? 'photo',
    x: round(init.x),
    y: round(init.y),
    w: round(init.w),
    h: round(init.h),
  };
  const withRole = init.role ? { ...base, role: init.role } : base;
  return init.panel ? { ...withRole, panel: init.panel } : withRole;
}

export interface GridOptions {
  cols: number;
  rows: number;
  /** gutter as a fraction of the SCOPE box; defaults to GUTTER_FRAC scaled for the scope */
  gx?: number;
  gy?: number;
  prefix?: string;
  /** stop after this many cells (for grids with an odd count) */
  take?: number;
}

/** Evenly divide `box` into a cols×rows grid of photo slots, in reading order. */
export function grid(box: Box, scope: LayoutScope, opts: GridOptions): LayoutSlot[] {
  const gx = opts.gx ?? GUTTER_FRAC * hx(scope);
  const gy = opts.gy ?? GUTTER_FRAC;
  const cw = (box.w - gx * (opts.cols - 1)) / opts.cols;
  const ch = (box.h - gy * (opts.rows - 1)) / opts.rows;
  const out: LayoutSlot[] = [];
  const limit = opts.take ?? opts.cols * opts.rows;
  for (let r = 0; r < opts.rows; r++) {
    for (let c = 0; c < opts.cols; c++) {
      if (out.length >= limit) return out;
      out.push(
        slot({
          id: `${opts.prefix ?? 'p'}${out.length + 1}`,
          x: box.x + c * (cw + gx),
          y: box.y + r * (ch + gy),
          w: cw,
          h: ch,
        }),
      );
    }
  }
  return out;
}

/** A horizontal band at the top/bottom of `box`, returning [band, remaining]. */
export function band(box: Box, height: number, where: 'top' | 'bottom', gap = GUTTER_FRAC): [Box, Box] {
  const h = height * box.h;
  const rest = { ...box, y: where === 'top' ? box.y + h + gap : box.y, h: box.h - h - gap };
  const strip = { ...box, y: where === 'top' ? box.y : box.y + box.h - h, h };
  return [strip, rest];
}

export interface LayoutInit {
  id: string;
  name: string;
  scope: LayoutScope;
  families: LayoutFamily[];
  tags: string[];
  slots: LayoutSlot[];
  marginFrac?: number;
}

/** Build a Layout, deriving photoCount / textCount from the slots. */
export function layout(init: LayoutInit): Layout {
  return {
    id: init.id,
    name: init.name,
    scope: init.scope,
    photoCount: init.slots.filter((s) => s.kind === 'photo').length,
    textCount: init.slots.filter((s) => s.kind === 'text').length,
    slots: init.slots,
    families: init.families,
    tags: init.tags,
    marginFrac: init.marginFrac ?? MARGIN_FRAC,
  };
}

/** Every format family — used by layouts that work anywhere. */
export const ALL_FAMILIES: LayoutFamily[] = ['square', 'portrait', 'landscape', 'panoramic'];
export const WIDE_FAMILIES: LayoutFamily[] = ['square', 'landscape', 'panoramic'];
export const TALL_FAMILIES: LayoutFamily[] = ['square', 'portrait'];
export const FULL_BOX: Box = FULL;
